import { forwardRef, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePanelist, usePreTask, useTask } from "../api";
import { getApiBaseUrl } from "../api/client";
import { getLaunchToken } from "../api/fetchers";
import { getFlexSessionId } from "../api/session";
import { ErrorState, SkeletonBlock } from "../components/ui/AsyncState";
import { Page } from "../components/ui/Page";
import { PretaskData, PreTaskModal } from "../components/ui/PreTaskModal";
import { usePanelPath } from "../routing";
import { estimatedMinutes, taskTagClass, taskTopic } from "../utils";
import { logError } from "../utils/logging";

export function TaskDetailPage() {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const panelPath = usePanelPath();
  const taskQuery = useTask(taskId);
  const panelistQuery = usePanelist();
  const preTaskQuery = usePreTask(taskId, { enabled: false });
  const [isPreTaskOpen, setIsPreTaskOpen] = useState(false);
  const [isSubmittingLaunch, setIsSubmittingLaunch] = useState(false);
  const [launchError, setLaunchError] = useState<Error | null>(null);
  const launchFormRef = useRef<HTMLFormElement | null>(null);

  if (taskQuery.isLoading || panelistQuery.isLoading) {
    return (
      <Page>
        <div className="task-detail-wrap">
          <SkeletonBlock count={6} height={42} />
        </div>
      </Page>
    );
  }

  if (
    taskQuery.error ||
    panelistQuery.error ||
    !taskQuery.data ||
    !panelistQuery.data
  ) {
    return (
      <Page>
        <div className="task-detail-wrap">
          <Link className="back-link" to={panelPath("/tasks")}>
            ← Back to tasks
          </Link>
          <ErrorState
            error={taskQuery.error ?? panelistQuery.error}
            title="Could not load task"
          />
        </div>
      </Page>
    );
  }

  const task = taskQuery.data;
  const panelist = panelistQuery.data;
  const balanceAfter = panelist.points + task.points;
  const preTaskQuestions = preTaskQuery.data?.questions ?? [];

  async function completeLaunch(preTaskAnswers: PretaskData) {
    if (!launchFormRef.current) {
      setLaunchError(new Error("Could not submit launch form."));
      return;
    }

    setLaunchError(null);
    setIsSubmittingLaunch(true);

    try {
      const form = launchFormRef.current;
      // const authToken = await getAuthToken();
      const launchToken = await getLaunchToken();
      const flexSessionId = getFlexSessionId();
      const setHeader = (field: string) =>
        form.elements.namedItem(`_header:${field}`);
      const preTaskInput = form.elements.namedItem("preTask");
      // const authorizationInput = setHeader("authorization");
      const launchTokenInput = setHeader("x-launch-token");
      const hostnameInput = setHeader("x-hostname");
      const flexSessionIdInput = setHeader("x-flex-session-id");

      if (
        !(preTaskInput instanceof HTMLInputElement) ||
        !(launchTokenInput instanceof HTMLInputElement) ||
        !(flexSessionIdInput instanceof HTMLInputElement) ||
        !(hostnameInput instanceof HTMLInputElement)
      ) {
        logError(new Error("Could not populate launch form."), {
          launchTokenInput,
          flexSessionIdInput,
          preTaskInput,
          taskId,
        });
        throw new Error("Could not populate launch form.");
      }

      form.action = `${getApiBaseUrl()}/tasks/${task.uid}/launch`;
      preTaskInput.value = JSON.stringify(preTaskAnswers);
      launchTokenInput.value = launchToken;
      flexSessionIdInput.value = flexSessionId ?? "";

      setIsPreTaskOpen(false);
      form.submit();
    } catch (error) {
      setIsSubmittingLaunch(false);
      setLaunchError(
        error instanceof Error ? error : new Error("Could not launch survey."),
      );
    }
  }

  function launchSurvey() {
    preTaskQuery.refetch().then((result) => {
      const questions = result.data?.questions ?? [];

      if (questions.length === 0) {
        void completeLaunch({ questions: {} });
        return;
      }

      setIsPreTaskOpen(true);
    });
  }

  return (
    <>
      <Page>
        <div className="task-detail-wrap">
          <Link className="back-link" to={panelPath("/tasks")}>
            ← Back to tasks
          </Link>
          <div>
            {task.tags?.map((tag) => (
              <span
                className={`tag ${taskTagClass(tag)}`}
                key={tag}
                style={{ display: "inline-block", marginBottom: 12 }}
              >
                {tag}
              </span>
            ))}
            <span
              className={`tag ${taskTagClass(taskTopic(task))}`}
              style={{ display: "inline-block", marginBottom: 12 }}
            >
              {taskTopic(task)}
            </span>
          </div>
          <h1
            style={{
              color: "var(--primary)",
              fontFamily: "var(--font-h)",
              fontSize: 28,
              fontWeight: 600,
              lineHeight: 1.1,
              marginBottom: 8,
            }}
          >
            {task.title}
          </h1>
          <div
            style={{
              color: "var(--accent)",
              fontFamily: "var(--font-h)",
              fontSize: 26,
              fontWeight: 600,
              marginBottom: 20,
            }}
          >
            {task.points} pts{" "}
            <span
              style={{ color: "var(--sand)", fontSize: 16, fontWeight: 400 }}
            >
              ≈ {task.gbpEquiv}
            </span>
          </div>

          <div className="td-grid">
            <TaskStat
              label="Estimated time"
              value={`~${estimatedMinutes(task)} minutes`}
            />
            <TaskStat
              label="Reward"
              value={`${task.points} pts (≈ ${task.gbpEquiv})`}
            />
            <TaskStat label="Topic" value={taskTopic(task)} />
            <TaskStat label="Balance after" value={`${balanceAfter} pts`} />
          </div>

          <hr className="div" />
          {!!task.description && (
            <>
              <p
                style={{
                  color: "var(--text)",
                  fontSize: 13.5,
                  fontWeight: 500,
                  marginBottom: 10,
                }}
              >
                What to expect
              </p>
              <div
                style={{
                  color: "var(--sand)",
                  fontSize: 13,
                  lineHeight: 1.8,
                  marginLeft: 18,
                }}
              >
                {task.description}
              </div>
            </>
          )}
          <hr className="div" />

          <div className="alert alert-info">
            <strong>Points credited after review</strong>
            Points appear in your balance once our team has reviewed your
            submission (usually 24-48 hours).
          </div>
          {(launchError || preTaskQuery.error) && (
            <ErrorState
              error={launchError ?? preTaskQuery.error}
              title="Could not launch survey"
            />
          )}

          <button
            className="btn btn-accent btn-full"
            disabled={isSubmittingLaunch || preTaskQuery.isFetching}
            onClick={launchSurvey}
            style={{ marginBottom: 10 }}
            type="button"
          >
            {isSubmittingLaunch || preTaskQuery.isFetching
              ? "Starting..."
              : "Start →"}
          </button>
          <Link className="btn btn-outline btn-full" to={panelPath("/tasks")}>
            Not right now
          </Link>
        </div>
      </Page>
      <TaskLaunchForm ref={launchFormRef} />
      <PreTaskModal
        onCancel={() => setIsPreTaskOpen(false)}
        onSubmit={completeLaunch}
        open={isPreTaskOpen}
        questions={preTaskQuestions}
      />
    </>
  );
}
export const TaskLaunchForm = forwardRef<
  HTMLFormElement,
  React.ComponentProps<"form">
>((props, launchFormRef) => {
  return (
    <form
      method="post"
      ref={launchFormRef}
      style={{ display: "none" }}
      {...props}
    >
      <input name="_header:x-launch-token" type="hidden" />
      <input name="_header:x-hostname" type="hidden" />
      <input name="_header:x-flex-session-id" type="hidden" />
      <input name="preTask" type="hidden" />
      <input name="turnstileToken" type="hidden" />
    </form>
  );
});

function TaskStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="td-stat">
      <div className="td-stat-lbl">{label}</div>
      <div className="td-stat-val">{value}</div>
    </div>
  );
}
